import { Button, InputBase, MenuItem, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Role } from '../../permissions/permissions';
import { darkTeal } from '../../theme/color-constants';
import { formatUserBasedOnDisplayNameAndLocale } from '../../utilFunctions/pure-utils';
import { getDomainDependenciesQueryKey } from '../../v2/api/domaindependencies/hooks';
import {
  isReservedRoleName,
  reservedRoleNameToTranslatedKeyMap,
} from '../../v2/api/role/utils';
import { ReservedRoleNames } from '../../v2/constants';
import {
  useAuthenticationServiceContext,
  useDomainContext,
  useDomainDependenciesServiceContext,
  useExecutorRoleContext,
  useExecutorRolesContext,
  useExecutorRoleSetterContext,
  useGlobalLocaleContext,
  useLocalStorageExecutorRoleServiceContext,
  useOriginalRoleViewAuthorizationServiceContext,
  useSimulatedRoleContext,
  useSimulatedRoleSetterContext,
  useUserRoleIdContext,
  useUserRolePreferencesSettingsContext,
  useUserRolesContext,
} from '../../v2/context/contexts';
import PopUpMenu from '../utils/PopUpMenu';

enum AccountMenus {
  ACCOUNT = 'account',
  SWITCH_ROLE = 'switch-role',
  CHANGE_ROLE = 'change-role',
}

const SelectedMenu = styled(MenuItem)<{
  $isSelected: boolean;
}>`
  background-color: ${(props) =>
    props.$isSelected ? `${darkTeal}!important` : 'inherit'};
`;

const SearchInput = styled(InputBase)`
  width: 100%;
  height: 25px !important;
  font-size: 0.8em;

  & .input {
    padding: 8px;
    transition: all 0.2s;
    height: 25px;
    width: 200px;
    color: white;
  }
`;

export const Account = observer(() => {
  const { t } = useTranslation();
  const originalRoleViewAuthorizationService =
    useOriginalRoleViewAuthorizationServiceContext();
  const userRoles = useUserRolesContext();
  const authService = useAuthenticationServiceContext();
  const executorRole = useExecutorRoleContext();
  const executorRoleSetter = useExecutorRoleSetterContext();
  const localStorageExecutorRoleService =
    useLocalStorageExecutorRoleServiceContext();
  const executorRoles = useExecutorRolesContext();
  const simulatedRole = useSimulatedRoleContext();
  const simulatedRoleSetter = useSimulatedRoleSetterContext();
  const userRoleId = useUserRoleIdContext();
  const locale = useGlobalLocaleContext();
  const domain = useDomainContext();
  const domainDependenciesService = useDomainDependenciesServiceContext();
  const userRolePreferencesSettings = useUserRolePreferencesSettingsContext();
  const menuRef = React.useRef<HTMLDivElement>(null);
  const anchorRef = React.useRef<HTMLElement | null>(null);
  const selectedRoleRef = React.useRef<Role | null>(null);
  const queryClient = useQueryClient();

  const selectedLanguage = locale.selected;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openMenu, setOpenMenu] = useState<AccountMenus | ''>('');
  const [searchRoleInput, setSearchRoleInput] = useState<string>('');

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    menuName: AccountMenus
  ) => {
    event.preventDefault();
    event.stopPropagation();
    anchorRef.current = event.currentTarget;
    setAnchorEl(event.currentTarget);
    setOpenMenu(menuName);
  };

  const handleChangeRoleMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    menuName: AccountMenus
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMenu(menuName);
  };

  const handleMenuClose = () => {
    if (selectedRoleRef.current) {
      executorRoleSetter(selectedRoleRef.current);
      selectedRoleRef.current = null;
    }
    setAnchorEl(null);
    anchorRef.current = null;
    setOpenMenu('');
  };

  const logout = () => {
    /**
     * When switching from role X to a different role (such as superadmin),
     * it's important to remove the executor role for X from local storage.
     * Otherwise, the app will retain the executor role for X after reloading,
     * which may not be the desired behavior. By removing the executor role for X,
     * the app will reset to the default view for the new role (e.g. the superadmin view).
     * */
    localStorageExecutorRoleService.clear();
    void authService.logOutUser(domain);
  };

  const clearCache = async () => {
    (await caches.keys()).forEach((cache) => caches.delete(cache));
    localStorage.clear();
    window.location.reload();
  };

  const switchRoleText = (currentlySimulatingRole: Role | null) => {
    if (currentlySimulatingRole != null) {
      return `${t('common:panalytRoleSwitcher.role')}: ${currentlySimulatingRole.name}`;
    } else {
      return `${t('common:panalytRoleSwitcher.switchRole')}`;
    }
  };

  const rolesThatCanBeSimulated = () => {
    return (userRoles || []).filter((role: Role) => {
      if (simulatedRole === null) {
        // Let's not show the user's own role (unless they are simulating a role that, itself, can simulate the user's own role)
        return role.id !== userRoleId;
      } else {
        // Let's not show the role that the user is currently already simulating
        return role.id !== simulatedRole.id;
      }
    });
  };

  const handleResetRole = () => {
    simulatedRoleSetter(null);
    // finally close menu
    handleMenuClose();
  };

  const handleSimulatedRoleSelection = async (role: Role) => {
    // NOTE: keep both executor role and simulated role updates together after the await
    // and within the same handler function to ensure that React batches them together within the same render cycle.
    const domainDependencies = await domainDependenciesService.get(role);
    queryClient.setQueryData(
      getDomainDependenciesQueryKey(domainDependenciesService, role),
      () => domainDependencies
    );

    const firstExecutorRole = domainDependencies?.executorRoles?.first();
    if (firstExecutorRole) {
      executorRoleSetter(firstExecutorRole);
    }
    simulatedRoleSetter(role);
    localStorageExecutorRoleService.clear();
    // finally close menu
    handleMenuClose();
  };

  const handleExecutorRoleSelection = (role: Role) => {
    selectedRoleRef.current = role;
    handleMenuClose();
    location.reload();
  };

  const setSearchRoleInputDebounced = debounce(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchRoleInput(e.target.value);
    }
  );

  // TODO: Valentin permissions - looks like we were explicitly removing
  const allExecutorRoles = executorRoles
    .slice()
    // sorting selected at the top, then alphabetical
    .sort((ra, rb) => {
      if (ra.id === executorRole.id) {
        return -1;
      } else if (rb.id === executorRole.id) {
        return 1;
      } else {
        return ra.name.localeCompare(rb.name);
      }
    });

  const searchedExecutorRoles = allExecutorRoles.filter(({ name }) => {
    return name.toLowerCase().includes(searchRoleInput.toLowerCase());
  });

  const roles = rolesThatCanBeSimulated().sort((r1, r2) =>
    r1.name.localeCompare(r2.name)
  );

  const accountMenuItems = (
    <>
      {
        <MenuItem
          onClick={(e) => handleChangeRoleMenuOpen(e, AccountMenus.CHANGE_ROLE)}
          className="intercom_appbar_change_roles"
        >
          {t('common:shell.account.changeRoles')}
        </MenuItem>
      }
      {originalRoleViewAuthorizationService.canManageAllDomains() && (
        <MenuItem
          onClick={(e) => handleChangeRoleMenuOpen(e, AccountMenus.SWITCH_ROLE)}
          className="intercom_appbar_simulate_roles"
        >
          <Typography
            variant="body2"
            sx={{
              color: 'secondary.main',
            }}
          >
            {switchRoleText(simulatedRole)}
          </Typography>
        </MenuItem>
      )}
      <MenuItem onClick={clearCache} className="intercom_appbar_clearcache">
        {t('common:shell.account.clearCache')}
      </MenuItem>
      <MenuItem onClick={logout} className="intercom_appbar_logout">
        {t('common:shell.account.logout')}
      </MenuItem>
      <Typography
        variant="body2"
        sx={{
          color: 'secondary.main',
          mx: 2,
          my: 1,
          'font-style': 'italic',
          'font-size': 'x-small',
        }}
      >
        {t('common:shell.app.version', {
          version: process.env.REACT_APP_GIT_SHA,
        })}
      </Typography>
    </>
  );

  const switchRoleMenuItems = (
    <>
      {simulatedRole !== null && (
        <MenuItem
          onClick={() => handleResetRole()}
          className="intercom_appbar_simulate_roles_reset_role"
        >
          {t('common:panalytRoleSwitcher.reset')}
        </MenuItem>
      )}
      {simulatedRole !== null && <hr />}
      {roles.length > 0 ? (
        roles.map((role: Role) => (
          <MenuItem
            key={role.id}
            onClick={() => handleSimulatedRoleSelection(role)}
            className="intercom_appbar_simulate_roles_select_role"
          >
            {role.name}
          </MenuItem>
        ))
      ) : (
        <MenuItem className="intercom_appbar_simulate_roles_select_role">
          {t('common:panalytRoleSwitcher.noRole')}
        </MenuItem>
      )}
    </>
  );

  const changeRoleMenuItems = (
    <>
      <SearchInput
        placeholder={t('common:shell.searchRoles')}
        classes={{ input: 'input' }}
        onChange={setSearchRoleInputDebounced}
        className={'intercom_change_roles_search'}
      />
      {searchedExecutorRoles.length > 0 ? (
        searchedExecutorRoles.map((role) => (
          <SelectedMenu
            key={role.id}
            onClick={() => handleExecutorRoleSelection(role)}
            className="intercom_appbar_permissions_change_roles"
            $isSelected={executorRole.id === role.id}
          >
            {isReservedRoleName(role.name)
              ? t(
                  reservedRoleNameToTranslatedKeyMap[
                    role.name as ReservedRoleNames
                  ]
                )
              : role.name}
          </SelectedMenu>
        ))
      ) : (
        <MenuItem className="intercom_appbar_permissions_change_roles">
          {t('common:panalytRoleSwitcher.noResult')}
        </MenuItem>
      )}
    </>
  );

  const menuContent = () => {
    if (openMenu === AccountMenus.ACCOUNT) {
      return accountMenuItems;
    }
    if (openMenu === AccountMenus.CHANGE_ROLE) {
      return changeRoleMenuItems;
    }
    if (openMenu === AccountMenus.SWITCH_ROLE) {
      return switchRoleMenuItems;
    }
    return null;
  };

  const profile = userRolePreferencesSettings?.settings?.profile;
  const name =
    userRolePreferencesSettings &&
    profile &&
    formatUserBasedOnDisplayNameAndLocale(profile, selectedLanguage);
  const displayName = name || authService.currentUser?.displayName || '';

  return (
    <>
      <Button
        onClick={(e) => handleMenuOpen(e, AccountMenus.ACCOUNT)}
        className="intercom_appbar_switch_role"
      >
        <Typography
          variant="body2"
          sx={{
            color: 'secondary.main',
          }}
        >
          {t('common:shell.hello')}
          {' ' + displayName + ''}
        </Typography>
      </Button>
      <div ref={menuRef}>
        <PopUpMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          placement="bottom-end"
          container={menuRef.current}
        >
          {menuContent()}
        </PopUpMenu>
      </div>
    </>
  );
});
